import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loading } from "@/components/bi/Loading";
import { EmptyState } from "@/components/bi/EmptyState";
import { Database, Table2, Sigma, Tags, Plus, Trash2, ExternalLink, Save, Loader2, FunctionSquare, Pencil } from "lucide-react";
import { effectiveType, typeIcon, isNumericType, defaultAggFor } from "@/lib/fieldWells";
import { MeasureEditorDialog, CalcFieldDialog } from "@/components/bi/CalcEditors";
import { RelationshipsPanel } from "@/components/bi/RelationshipsPanel";
import { FieldProfilePanel } from "@/components/bi/FieldProfilePanel";
import { ModelCanvas } from "@/components/bi/ModelCanvas";

function DatasetPicker({ datasets, value, onChange }) {
  return (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger data-testid="workspace-dataset-select" className="h-9 w-[260px] bg-[color:var(--surface)]"><SelectValue placeholder="Pick a dataset" /></SelectTrigger>
      <SelectContent>{datasets.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
    </Select>
  );
}

// ---------------- Data view: inspect rows & schema without leaving the builder ----------------
export function DataViewPanel({ datasets, defaultDatasetId, onDatasetsChanged }) {
  const [dsId, setDsId] = useState(defaultDatasetId || datasets[0]?.id || "");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("rows");
  const [stats, setStats] = useState(null);
  const [modelDoc, setModelDoc] = useState(null);
  const [profileField, setProfileField] = useState(null);
  const [calcOpen, setCalcOpen] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [freezeFirst, setFreezeFirst] = useState(false);
  const fieldMeta = modelDoc?.field_meta || {};
  useEffect(() => {
    if (!dsId) return;
    setLoading(true); setPreview(null); setStats(null); setProfileField(null);
    api.get(`/datasets/${dsId}/preview`, { params: { limit: 100 } })
      .then(({ data }) => setPreview(data)).catch((e) => toast.error(e?.response?.data?.detail || "Failed to load data preview"))
      .finally(() => setLoading(false));
    api.get(`/datasets/${dsId}/model`).then(({ data }) => setModelDoc(data)).catch(() => setModelDoc(null));
  }, [dsId]);
  const loadMore = () => {
    if (!preview || loadingMore) return;
    setLoadingMore(true);
    api.get(`/datasets/${dsId}/preview`, { params: { limit: 500, offset: preview.rows.length } })
      .then(({ data }) => setPreview((p) => ({ ...p, rows: [...(p?.rows || []), ...(data.rows || [])] })))
      .catch(() => toast.error("Failed to load more rows"))
      .finally(() => setLoadingMore(false));
  };
  useEffect(() => {
    if (tab !== "fields" || !dsId || stats) return;
    api.get(`/datasets/${dsId}/field-stats`).then(({ data }) => setStats(data)).catch(() => toast.error("Failed to profile fields"));
  }, [tab, dsId, stats]);

  const ds = datasets.find((d) => d.id === dsId);
  const schema = ds?.schema || [];
  const statMap = Object.fromEntries((stats?.fields || []).map((f) => [f.name, f]));

  return (
    <div data-testid="data-view-panel" className="h-full w-full flex-1 min-w-0 flex flex-col p-4 gap-3 min-h-0">
      <div className="flex items-center gap-3 shrink-0 flex-wrap">
        <Table2 className="w-4 h-4 text-[color:var(--brand-ocean)]" />
        <span className="font-display text-sm text-[color:var(--ink)]">Data view</span>
        <DatasetPicker datasets={datasets} value={dsId} onChange={setDsId} />
        <div className="flex items-center rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface)] p-0.5">
          {[{ v: "rows", label: "Rows" }, { v: "fields", label: "Fields" }].map((t) => (
            <button key={t.v} data-testid={`data-view-tab-${t.v}`} onClick={() => setTab(t.v)}
              className={"text-[11px] px-2.5 py-1 rounded-md transition-colors " + (tab === t.v ? "bg-[color:var(--brand-ocean)] text-white" : "text-[color:var(--ink-3)] hover:text-[color:var(--ink)]")}>{t.label}</button>
          ))}
        </div>
        {preview && <span className="text-xs text-[color:var(--ink-3)]">Showing {(preview.rows || []).length.toLocaleString()} of {preview.row_count?.toLocaleString()} rows · {preview.schema?.length} columns{stats?.sampled ? " · profiled on 50k sample" : ""}</span>}
        {tab === "rows" && (
          <label className="flex items-center gap-1.5 text-[11px] text-[color:var(--ink-3)] cursor-pointer ml-auto">
            <input data-testid="data-view-freeze-first" type="checkbox" checked={freezeFirst} onChange={(e) => setFreezeFirst(e.target.checked)} className="accent-[#2B4EFF]" />
            Freeze first column
          </label>
        )}
      </div>
      {!dsId ? <EmptyState icon={Database} title="Pick a dataset" description="Choose a dataset to inspect its rows." /> :
        loading ? <Loading label="Loading data" /> : tab === "rows" ? (preview && (
          <div data-testid="data-view-scroll" className="flex-1 min-h-0 min-w-0 w-full rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--surface)] overflow-x-auto overflow-y-auto overscroll-contain" style={{ scrollbarWidth: "thin" }}>
            <table className="text-xs w-max min-w-full">
              <thead className="sticky top-0 bg-[color:var(--surface-2)] z-10"><tr>{(preview.columns || []).map((c, ci) => <th key={c} className={"text-left px-3 py-2.5 font-medium text-[color:var(--ink-2)] whitespace-nowrap" + (freezeFirst && ci === 0 ? " sticky left-0 z-20 bg-[color:var(--surface-2)] shadow-[2px_0_4px_rgba(10,16,41,0.06)]" : "")}>{fieldMeta[c]?.display_name || c}</th>)}</tr></thead>
              <tbody>{(preview.rows || []).map((r, i) => <tr key={i} className="border-t border-[color:var(--glass-border)] hover:bg-[color:var(--glass-bg)]">{(preview.columns || []).map((c, ci) => <td key={c} className={"px-3 py-1.5 text-[color:var(--ink-2)] whitespace-nowrap max-w-[420px] truncate" + (freezeFirst && ci === 0 ? " sticky left-0 z-[5] bg-[color:var(--surface)] shadow-[2px_0_4px_rgba(10,16,41,0.06)]" : "")} title={String(r[c] ?? "")}>{String(r[c] ?? "—")}</td>)}</tr>)}</tbody>
            </table>
            {(preview.rows || []).length < (preview.row_count || 0) && (
              <div className="p-2.5 border-t border-[color:var(--glass-border)] text-center sticky left-0">
                <button data-testid="data-view-load-more" onClick={loadMore} disabled={loadingMore}
                  className="text-[11px] px-3 py-1.5 rounded-md border border-[color:var(--glass-border)] text-[color:var(--brand-ocean)] hover:bg-[color:var(--surface-2)] disabled:opacity-50">
                  {loadingMore ? "Loading…" : `Load 500 more rows (${((preview.row_count || 0) - preview.rows.length).toLocaleString()} remaining)`}
                </button>
              </div>
            )}
          </div>
        )) : (
          <div className="flex-1 min-h-0 min-w-0 flex gap-3">
          <div data-testid="schema-explorer" className="flex-1 min-h-0 min-w-0 rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--surface)] overflow-auto overscroll-contain" style={{ scrollbarWidth: "thin" }}>
            <table className="text-xs w-max min-w-full">
              <thead className="sticky top-0 bg-[color:var(--surface-2)] z-10">
                <tr>{["Field", "Source column", "Type", "Classification", "Default agg", "Unique", "Nulls", "Sample values"].map((h) => <th key={h} className="text-left px-3 py-2.5 font-medium text-[color:var(--ink-2)] whitespace-nowrap">{h}</th>)}</tr>
              </thead>
              <tbody>
                {schema.map((c) => {
                  const eff = effectiveType(c.name, schema, fieldMeta);
                  const Icon = typeIcon(eff);
                  const st = statMap[c.name];
                  const numeric = isNumericType(eff);
                  return (
                    <tr key={c.name} data-testid="schema-explorer-row" onClick={() => setProfileField(c.name)}
                      className={"border-t border-[color:var(--glass-border)] cursor-pointer hover:bg-[color:var(--glass-bg)] " + (fieldMeta[c.name]?.hidden ? "opacity-45 " : "") + (profileField === c.name ? "bg-[color:rgba(43,78,255,0.06)]" : "")}>
                      <td className="px-3 py-2 whitespace-nowrap"><span className="flex items-center gap-1.5 text-[color:var(--ink)]"><Icon className="w-3 h-3 text-[color:var(--brand-ocean)]" />{fieldMeta[c.name]?.display_name || c.name}{fieldMeta[c.name]?.hidden ? " (hidden)" : ""}</span></td>
                      <td className="px-3 py-2 font-mono text-[color:var(--ink-3)] whitespace-nowrap">{c.name}</td>
                      <td className="px-3 py-2 text-[color:var(--ink-2)]">{eff}{fieldMeta[c.name]?.type_override ? "*" : ""}</td>
                      <td className="px-3 py-2 text-[color:var(--ink-2)]">{numeric && defaultAggFor(c.name, schema, fieldMeta) !== "none" ? "Measure" : "Dimension"}</td>
                      <td className="px-3 py-2 text-[color:var(--ink-2)]">{defaultAggFor(c.name, schema, fieldMeta)}</td>
                      <td className="px-3 py-2 tabular-nums text-[color:var(--ink-2)]">{st ? st.unique_count.toLocaleString() : "…"}</td>
                      <td className="px-3 py-2 tabular-nums text-[color:var(--ink-2)]">{st ? st.null_count.toLocaleString() : "…"}</td>
                      <td className="px-3 py-2 text-[color:var(--ink-3)] max-w-[340px] truncate">{st ? st.samples.map(String).join(" · ") : "…"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {profileField && modelDoc && ds && (
            <FieldProfilePanel ds={ds} field={profileField} model={modelDoc}
              onModelChange={setModelDoc} onClose={() => setProfileField(null)}
              onDatasetsChanged={(opts) => { if (opts?.openCalc) setCalcOpen(true); else onDatasetsChanged && onDatasetsChanged(); }} />
          )}
          </div>
        )}
      <CalcFieldDialog open={calcOpen} onOpenChange={setCalcOpen} dataset={ds}
        onSaved={() => { setCalcOpen(false); onDatasetsChanged && onDatasetsChanged(); }} />
    </div>
  );
}

// ---------------- Model workspace: relationship canvas + per-dataset semantic model ----------------
export function ModelWorkspacePanel({ datasets, defaultDatasetId, onContractsChanged, onDatasetsChanged }) {
  const [tab, setTab] = useState("canvas");
  return (
    <div data-testid="model-workspace" className="flex-1 min-w-0 w-full h-full flex flex-col p-4 gap-3 min-h-0">
      <div className="flex items-center gap-3 shrink-0 flex-wrap">
        <Sigma className="w-4 h-4 text-[color:var(--brand-ocean)]" />
        <span className="font-display text-sm text-[color:var(--ink)]">Model</span>
        <div className="flex items-center rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface)] p-0.5">
          {[{ v: "canvas", label: "Relationships" }, { v: "dataset", label: "Dataset model" }].map((t) => (
            <button key={t.v} data-testid={`model-ws-tab-${t.v}`} onClick={() => setTab(t.v)}
              className={"text-[11px] px-2.5 py-1 rounded-md transition-colors " + (tab === t.v ? "bg-[color:var(--brand-ocean)] text-white" : "text-[color:var(--ink-3)] hover:text-[color:var(--ink)]")}>{t.label}</button>
          ))}
        </div>
        {tab === "canvas" && <span className="text-[11px] text-[color:var(--ink-3)]">Relationships let one visual mix fields from several datasets.</span>}
      </div>
      {tab === "canvas" ? (
        <ModelCanvas datasets={datasets} onContractsChanged={onContractsChanged} onDatasetsChanged={onDatasetsChanged} />
      ) : (
        <div className="flex-1 min-h-0 -m-4 mt-0">
          <ModelViewPanel datasets={datasets} defaultDatasetId={defaultDatasetId} />
        </div>
      )}
    </div>
  );
}

// ---------------- Model view: quick semantic-model editing ----------------
const AGGS = ["sum", "avg", "min", "max", "count", "count_distinct", "counta", "median", "product", "stddev", "stddevp", "var", "varp"];

export function ModelViewPanel({ datasets, defaultDatasetId }) {
  const nav = useNavigate();
  const [dsId, setDsId] = useState(defaultDatasetId || datasets[0]?.id || "");
  const [model, setModel] = useState(null);
  const [schema, setSchema] = useState([]);
  const [saving, setSaving] = useState(false);
  const [measDialog, setMeasDialog] = useState(null);   // {editMeasure} | null
  useEffect(() => {
    if (!dsId) return;
    setModel(null);
    api.get(`/datasets/${dsId}/model`).then(({ data }) => {
      setSchema(data.dataset_schema || []);
      const { id, dataset_id, user_id, tenant_id, version, created_at, updated_at, dataset_schema, ...rest } = data;
      setModel({
        name: rest.name || "Model", description: rest.description || "", time_dimension: rest.time_dimension || null,
        dimensions: rest.dimensions || [], measures: rest.measures || [], hierarchies: rest.hierarchies || [],
        rls_rules: rest.rls_rules || [], relationships: rest.relationships || [], field_meta: rest.field_meta || {},
      });
    }).catch(() => toast.error("Could not load semantic model"));
  }, [dsId]);

  const numericFields = useMemo(() => schema.filter((c) => c.type === "number"), [schema]);

  const save = async () => {
    setSaving(true);
    try { await api.put(`/datasets/${dsId}/model`, model); toast.success("Model saved — widgets refresh on next query"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const updMeasure = (i, patch) => setModel((m) => ({ ...m, measures: m.measures.map((x, j) => j === i ? { ...x, ...patch } : x) }));
  const addMeasure = () => setModel((m) => ({
    ...m,
    measures: [...m.measures, { key: `measure_${m.measures.length + 1}`, label: "New measure", kind: "basic", field: numericFields[0]?.name || "", agg: "sum", visible: true }],
  }));
  const delMeasure = (i) => setModel((m) => ({ ...m, measures: m.measures.filter((_, j) => j !== i) }));
  const updDim = (i, patch) => setModel((m) => ({ ...m, dimensions: m.dimensions.map((x, j) => j === i ? { ...x, ...patch } : x) }));

  return (
    <div data-testid="model-view-panel" className="h-full flex flex-col p-4 gap-3 min-h-0">
      <div className="flex items-center gap-3 shrink-0 flex-wrap">
        <Sigma className="w-4 h-4 text-[color:var(--brand-ocean)]" />
        <span className="font-display text-sm text-[color:var(--ink)]">Model view</span>
        <DatasetPicker datasets={datasets} value={dsId} onChange={setDsId} />
        <div className="flex-1" />
        {dsId && <Button data-testid="open-model-studio" size="sm" variant="outline" className="bg-[color:var(--surface)]" onClick={() => nav(`/model/${dsId}`)}><ExternalLink className="w-3.5 h-3.5 mr-1" />Full Model Studio</Button>}
        {model && <Button data-testid="model-quick-save" size="sm" onClick={save} disabled={saving} className="bg-[color:var(--brand-ocean)] text-white">{saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />}Save model</Button>}
      </div>
      {!dsId ? <EmptyState icon={Database} title="Pick a dataset" description="Choose a dataset to manage its measures & dimensions." /> :
        !model ? <Loading label="Loading model" /> : (
          <ScrollArea className="flex-1 min-h-0">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 pr-2">
              <div className="rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--surface)] p-3">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs text-[color:var(--ink-2)] flex items-center gap-1"><Sigma className="w-3.5 h-3.5" />Measures ({model.measures.length})</Label>
                  <div className="flex items-center gap-1">
                    <Button data-testid="model-add-calc-measure" size="sm" variant="ghost" className="h-6 text-[color:var(--brand-mint)]" onClick={() => setMeasDialog({ editMeasure: null })}><FunctionSquare className="w-3.5 h-3.5 mr-0.5" />ƒx</Button>
                    <Button data-testid="model-add-measure" size="sm" variant="ghost" className="h-6 text-[color:var(--brand-ocean)]" onClick={addMeasure}><Plus className="w-3.5 h-3.5" />Add</Button>
                  </div>
                </div>
                <div className="space-y-2">
                  {model.measures.map((ms, i) => (
                    <div key={ms.key || i} data-testid="model-measure-row" className="rounded-lg bg-[color:var(--surface-2)] p-2 space-y-1.5">
                      <div className="flex items-center gap-1.5">
                        <Input value={ms.label || ""} onChange={(e) => updMeasure(i, { label: e.target.value })} className="h-7 text-xs bg-[color:var(--surface)] flex-1" placeholder="Label" />
                        <Switch checked={ms.visible !== false} onCheckedChange={(v) => updMeasure(i, { visible: v })} title="Visible to authors" />
                        <button onClick={() => delMeasure(i)} className="text-[color:var(--ink-3)] hover:text-[color:var(--danger)] p-0.5"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                      {ms.kind === "calculated" ? (
                        <button data-testid="model-edit-calc-measure" onClick={() => setMeasDialog({ editMeasure: ms })}
                          className="w-full flex items-center gap-1.5 h-7 px-2 rounded-md bg-[color:var(--surface)] text-[11px] font-mono text-[color:var(--ink-2)] hover:border-[color:var(--brand-ocean)] border border-transparent text-left">
                          <FunctionSquare className="w-3 h-3 shrink-0 text-[color:var(--brand-mint)]" />
                          <span className="truncate flex-1">{ms.expression || "Edit expression…"}</span>
                          <Pencil className="w-3 h-3 shrink-0 text-[color:var(--ink-3)]" />
                        </button>
                      ) : (
                        <div className="flex gap-1.5">
                          <Select value={ms.field || ""} onValueChange={(v) => updMeasure(i, { field: v })}>
                            <SelectTrigger className="h-7 text-xs bg-[color:var(--surface)] flex-1 min-w-0"><SelectValue placeholder="Field" /></SelectTrigger>
                            <SelectContent>{schema.map((c) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                          </Select>
                          <Select value={ms.agg || "sum"} onValueChange={(v) => updMeasure(i, { agg: v })}>
                            <SelectTrigger className="h-7 text-xs bg-[color:var(--surface)] w-[110px] shrink-0"><SelectValue /></SelectTrigger>
                            <SelectContent>{AGGS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  ))}
                  {model.measures.length === 0 && <p className="text-xs text-[color:var(--ink-3)]">No measures yet — add one to enable governed widgets.</p>}
                </div>
              </div>
              <div className="rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--surface)] p-3">
                <Label className="text-xs text-[color:var(--ink-2)] flex items-center gap-1 mb-2"><Tags className="w-3.5 h-3.5" />Dimensions ({model.dimensions.length})</Label>
                <div className="space-y-1.5">
                  {model.dimensions.map((d, i) => (
                    <div key={d.key || i} className="flex items-center gap-1.5 rounded-lg bg-[color:var(--surface-2)] px-2 py-1.5">
                      <Input value={d.label || ""} onChange={(e) => updDim(i, { label: e.target.value })} className="h-7 text-xs bg-[color:var(--surface)] flex-1" />
                      <span className="text-[10px] text-[color:var(--ink-3)] font-mono truncate max-w-[110px]">{d.field}</span>
                      <Switch checked={d.visible !== false} onCheckedChange={(v) => updDim(i, { visible: v })} title="Visible to authors" />
                    </div>
                  ))}
                  {model.dimensions.length === 0 && <p className="text-xs text-[color:var(--ink-3)]">No dimensions defined.</p>}
                </div>
                <p className="text-[10px] text-[color:var(--ink-3)] mt-3">Hierarchies, row-level security and joins live in the full Model Studio.</p>
              </div>
              <div className="lg:col-span-2"><RelationshipsPanel datasets={datasets} dsId={dsId} /></div>
            </div>
          </ScrollArea>
        )}
      <MeasureEditorDialog open={!!measDialog} onOpenChange={(v) => !v && setMeasDialog(null)}
        datasetId={dsId} model={model} editMeasure={measDialog?.editMeasure}
        onSaved={(data) => {
          const { id, dataset_id, user_id, tenant_id, version, created_at, updated_at, dataset_schema, ...rest } = data;
          setModel({
            name: rest.name || "Model", description: rest.description || "", time_dimension: rest.time_dimension || null,
            dimensions: rest.dimensions || [], measures: rest.measures || [], hierarchies: rest.hierarchies || [],
            rls_rules: rest.rls_rules || [], relationships: rest.relationships || [], field_meta: rest.field_meta || {},
          });
        }} />
    </div>
  );
}
